import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
} from '@angular/core'
import {
    EI_BUTTON_DEFAULTS,
    EI_BUTTON_ICON_POSITION,
} from '@app/shared/constants/ei-button.constants'
import { SvgIconComponent } from '@app/shared/components/svg-icon/svg-icon.component'

type TEiButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'warn'
type TEiButtonSize = 'sm' | 'md'
type TEiButtonIconPosition = (typeof EI_BUTTON_ICON_POSITION)[keyof typeof EI_BUTTON_ICON_POSITION]

@Component({
    selector: 'ei-button',
    standalone: true,
    imports: [SvgIconComponent],
    templateUrl: './ei-button.component.html',
    styleUrl: './ei-button.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.ei-button--full-width]': 'fullWidth()',
    },
})
export class EiButtonComponent {
    variant = input<TEiButtonVariant>(EI_BUTTON_DEFAULTS.variant)
    size = input<TEiButtonSize>(EI_BUTTON_DEFAULTS.size)
    iconPosition = input<TEiButtonIconPosition>(EI_BUTTON_DEFAULTS.iconPosition)
    type = input<'button' | 'submit' | 'reset'>(EI_BUTTON_DEFAULTS.type)
    position = input<'left' | 'right' | null>(null)
    textTransform = input<string>()

    icon = input<string>()
    label = input<string>()

    disabled = input(false, { transform: booleanAttribute })
    /** Shows a three-dot loader and hides label text; the control is non-interactive while loading. */
    loading = input(false, { transform: booleanAttribute })
    /** Stretches the host and inner button to 100% of the parent width. */
    fullWidth = input(false, { transform: booleanAttribute })
    ariaLabel = input<string>()

    buttonClick = output<MouseEvent>()

    isIconSrc = computed(() => {
        const i = this.icon() ?? ''
        return i.includes('/') || i.endsWith('.svg')
    })

    isIconOnly = computed(() => this.iconPosition() === EI_BUTTON_ICON_POSITION.ONLY)

    showLeftIcon = computed(
        () => !!this.icon() && this.iconPosition() === EI_BUTTON_ICON_POSITION.LEFT,
    )

    showRightIcon = computed(
        () => !!this.icon() && this.iconPosition() === EI_BUTTON_ICON_POSITION.RIGHT,
    )

    showText = computed(() => !this.isIconOnly())

    buttonClassList = computed(() => {
        const classNames = [`ei-btn--${this.variant()}`, `ei-btn--${this.size()}`]
        if (this.isIconOnly()) classNames.push('ei-btn--icon-only')
        if (this.loading()) classNames.push('ei-btn--loading')
        if (this.fullWidth()) classNames.push('ei-btn--full-width')
        const textTransform = this.textTransform()
        if (textTransform) classNames.push(`ei-btn--${textTransform}`)
        return classNames.join(' ')
    })

    computedAriaLabel = computed(() => {
        if (!this.isIconOnly()) return null
        return this.ariaLabel() || EI_BUTTON_DEFAULTS.ariaLabelFallback
    })

    /** When loading, visible text is removed — expose an accessible name on the button. */
    buttonAriaLabel = computed((): string | null => {
        if (this.loading()) {
            const explicit = this.ariaLabel()?.trim()
            if (explicit) return explicit
            const lbl = this.label()
            if (lbl != null && String(lbl).trim() !== '') {
                return `${String(lbl).trim()} (loading)`
            }
            if (this.isIconOnly()) {
                return `${this.ariaLabel() || EI_BUTTON_DEFAULTS.ariaLabelFallback} (loading)`
            }
            return 'Loading'
        }
        return this.computedAriaLabel()
    })

    isBusy = computed(() => this.disabled() || this.loading())

    onClick(event: MouseEvent): void {
        if (this.isBusy()) return
        this.buttonClick.emit(event)
    }
}
